import type { QueriesObject, QueryResults, System } from '../../odie';

import { createCollision } from '../actions/collisions/collisionFactory';
import { BlockComponent } from '../components/BlockComponent';
import { CollideEvent, isOneHitCollider } from '../defs/collide';
import { type BlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { blockIsBinded, blockIsFrozen } from '../util/blockTools';

/*
    handles block collisions
*/
export class CollisionSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'collision';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent],
      modified: true,
    },
  };

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;

  // impl
  //-------------------------------------------------------------------------
  public modifiedQuery(
    entity: BlockEntity,
    component: BlockComponent,
    properties: any,
  ) {
    // handle collide event
    if (properties.collide) {
      this._handleCollide(entity, properties.collide as CollideEvent);
    }
  }

  // private
  //-------------------------------------------------------------------------
  private async _handleCollide(entity: BlockEntity, event: CollideEvent) {
    // Check if block can be collided - not "shielded" by overlay blocks like ice
    const canCollide =
      !blockIsFrozen(this.scene, entity) && !blockIsBinded(this.scene, entity);

    // One-hit-destroy colliders can pierce through frozen and binded blocks
    const oneHitDestroy = isOneHitCollider(event.collider?.type);

    // If not collidable and not one-hit-destroy collision, skip
    if (!canCollide && !oneHitDestroy) {
      return;
    }

    // Create collision - if not effective, skip
    const collision = createCollision(this.scene, entity);
    if (!collision) {
      return;
    }

    const phase = this.scene.sessionEntity.c.phase;

    // push activations
    phase.activePush();

    // resolve collision
    const resolved = await collision.resolve(event);

    // pop activations
    await phase.activePop();

    // if resolved on tap, then trigger moved state
    if (resolved && event.id === 'tap') phase.triggerMove();
  }
}
